import { UserCog } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { clearImpersonationToken } from "../../utils/resolveAuthToken";
import { authFetch } from "../../utils/authFetch";

/**
 * Band shown while a super admin is acting as another user. Wraps the
 * authenticated surface, mirroring ShareBanner's structure so the band is a
 * `shrink-0` sibling of a `flex-1 overflow-hidden` content box inside one
 * `h-screen` column — a band stacked *above* an h-screen shell would push the
 * document past the viewport.
 *
 * Driven ONLY by /api/me's server-verified `impersonation` block, never by
 * sessionStorage: a banner a script can suppress is how an unmarked session
 * happens.
 *
 * It matters more in Cortex than in the sibling platforms. BrandingContext.tsx
 * repaints the entire app as Joveo the moment userType reads "external", so the
 * app stops looking unusual and starts looking like a partner's. This band is
 * the only thing that distinguishes the two.
 */
export default function ImpersonationBanner({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const session = user?.impersonation;

  if (!session) return <>{children}</>;

  const ends = new Date(session.expiresAt).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });

  const stop = async () => {
    try {
      await authFetch("/api/admin/impersonation", { method: "DELETE" });
    } finally {
      // Clear and reload either way: a failed DELETE still leaves a token this
      // tab must stop using, and the session lapses on its own regardless.
      clearImpersonationToken();
      window.location.reload();
    }
  };

  return (
    <div className="flex flex-col h-screen">
      <div
        role="status"
        className="shrink-0 bg-amber-50 border-b border-amber-200 px-4 py-1.5 flex items-center gap-2 text-[11px] text-amber-800"
      >
        <UserCog className="size-3.5" />
        <span className="font-medium tracking-wide">Viewing as</span>
        <span className="font-mono">{session.targetEmail}</span>
        <span className="text-amber-400">·</span>
        <span>ends {ends}</span>
        <span className="text-amber-400">·</span>
        <button
          type="button"
          onClick={stop}
          className="font-medium underline hover:no-underline"
        >
          Stop
        </button>
      </div>
      <div className="flex-1 overflow-hidden">{children}</div>
    </div>
  );
}
